import { db } from '@shared/services/firebase';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { Capacitor } from '@capacitor/core';
import { APP_VERSION } from '@/version';

/**
 * Record the resolved Remote Config flag snapshot a user was exposed to.
 *
 * Bare RC flags (dailySessionEnabled, foundMoneyEnabled, …) resolve client-side
 * and leave no Firestore trail — unlike experiment-framework assignments, which
 * stamp `e_experiments.{key}` ([[record-experiment-assignment]]). That leaves
 * internal (Firestore-based) readers with no exposed denominator for any flag
 * not wired as an experiment. This writes an append-only change log at
 * `userContent/{uid}/rcExposures/{autoId}`: one row per DISTINCT resolved
 * snapshot per device, so the log stays tiny (a user whose flags never change
 * writes exactly one row per device, ever).
 *
 * Write economy — RC resolves on every app load, so a naive per-load write is a
 * non-starter. Three dedupe layers, cheapest first:
 *   1. in-memory last-written hash (guards same-session flicker/re-renders)
 *   2. localStorage last-written hash per uid (guards across sessions on the
 *      same device — no Firestore read needed to skip the write)
 *   3. if storage is unavailable, a sessionStorage fallback caps it at one
 *      write per tab session
 *
 * The per-user "current" snapshot is simply the latest row by resolvedAt;
 * "who had flag X during window W" is a collection-group read over rcExposures
 * (left to read-time tooling / rollups — no speculative index shipped here).
 *
 * Anon→account carry mirrors experiments: rows written under a pre-signup anon
 * uid are copied onto the account uid server-side by the identity reconcilers
 * (`enough_reconcile_experiment_assignments` orchestrator).
 *
 * Best-effort: failures are swallowed so analytics plumbing never blocks the UI.
 */

/** Resolved flag map — RemoteConfigValues after per-user overrides. */
export type RcFlagMap = Record<string, boolean | number | string>;

/**
 * Canonical string form of a flag map: JSON with sorted keys, so hashing is
 * insensitive to object key order.
 */
export function canonicalFlagString(flags: RcFlagMap): string {
  const sorted: RcFlagMap = {};
  for (const key of Object.keys(flags).sort()) {
    sorted[key] = flags[key];
  }
  return JSON.stringify(sorted);
}

/** FNV-1a 32-bit hex hash — cheap, sync, stable; collision risk is irrelevant
 * here (a collision just skips one log row for one user). */
export function hashFlags(canonical: string): string {
  let hash = 0x811c9dc5;
  for (let i = 0; i < canonical.length; i++) {
    hash ^= canonical.charCodeAt(i);
    hash = Math.imul(hash, 0x01000193);
  }
  return (hash >>> 0).toString(16).padStart(8, '0');
}

const lastHashKey = (uid: string) => `enough_rc_exposure_${uid}`;
const SESSION_ID_KEY = 'enough_session_id';

/** Stable id for this tab session, shared with any future session-scoped events. */
export function getOrCreateSessionId(): string {
  try {
    let id = sessionStorage.getItem(SESSION_ID_KEY);
    if (!id) {
      id = typeof crypto !== 'undefined' && 'randomUUID' in crypto
        ? crypto.randomUUID()
        : `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
      sessionStorage.setItem(SESSION_ID_KEY, id);
    }
    return id;
  } catch {
    return 'unknown';
  }
}

/** In-memory guard: last hash written per uid this JS lifetime. */
const writtenThisLifetime = new Map<string, string>();

/**
 * Decide whether `hash` needs writing for `uid`, consulting (and updating) the
 * dedupe layers. Exported for tests.
 */
export function shouldRecordExposure(uid: string, hash: string): boolean {
  if (writtenThisLifetime.get(uid) === hash) return false;
  try {
    if (localStorage.getItem(lastHashKey(uid)) === hash) {
      writtenThisLifetime.set(uid, hash);
      return false;
    }
    return true;
  } catch {
    // localStorage unavailable — fall back to once per tab session per hash.
    try {
      const key = `${lastHashKey(uid)}_${hash}`;
      if (sessionStorage.getItem(key)) return false;
      sessionStorage.setItem(key, '1');
      return true;
    } catch {
      // No storage at all: the in-memory map above still caps per-lifetime.
      return true;
    }
  }
}

/** Mark `hash` as durably written for `uid`. Exported for tests. */
export function markExposureRecorded(uid: string, hash: string): void {
  writtenThisLifetime.set(uid, hash);
  try {
    localStorage.setItem(lastHashKey(uid), hash);
  } catch { /* best-effort */ }
}

/** Test-only: reset the in-memory dedupe layer. */
export function _resetExposureMemory(): void {
  writtenThisLifetime.clear();
}

export function recordRcExposure(uid: string, flags: RcFlagMap): void {
  if (!uid) return;
  const canonical = canonicalFlagString(flags);
  const hash = hashFlags(canonical);
  if (!shouldRecordExposure(uid, hash)) return;
  // Mark before the async write so a re-render burst can't double-write; on
  // failure the worst case is a missed row, never a duplicate storm.
  markExposureRecorded(uid, hash);
  addDoc(collection(db, 'userContent', uid, 'rcExposures'), {
    flags,
    hash,
    appVersion: APP_VERSION,
    platform: Capacitor.getPlatform(), // 'web' | 'ios' | 'android'
    sessionId: getOrCreateSessionId(),
    resolvedAt: new Date().toISOString(),
    createdAt: serverTimestamp(),
  }).catch(() => {});
}
